import React, { forwardRef, ForwardedRef, Ref } from 'react';
import { useListState } from 'react-stately/useListState';
import { useThemeCheck } from '../../utils';
import { StatelessListBoxProps, StatelessListBox } from './StatelessListBox';
import { Node } from '@react-types/shared';

import { groupSelectedItems } from '../../utils/groupSelectedItems';
import patchSelectionManager from '../../utils/patchSelectionManager';
import { WithResolveSelectAllToKeysKeys } from '../../utils/patchSelectionManager';

export type ListBoxProps<T> = WithResolveSelectAllToKeysKeys<
  Omit<StatelessListBoxProps<T>, 'state' | 'disableSelectedStyles'> &
    // Since we're not supporting a visible label, we must enforce one of the aria ones
    ({ 'aria-labelledby': string } | { 'aria-label': string }) & {
      /**
       * Whether selected items should be grouped in a section at the top,
       * @default true
       */
      groupSelectedItemsOnTop?: boolean;
      /**
       * Filter for the items.
       * @param textValue - The `textValue` of the current node to filter.
       */
      filter?: (textValue: Node<T>['textValue']) => boolean;
    }
>;

const recursiveFilter = <T extends object>(
  nodes: Iterable<Node<T>>,
  filter: Required<ListBoxProps<T>>['filter']
): Node<T>[] =>
  Array.from(nodes).flatMap((node) => {
    if (node.type === 'section' && node.hasChildNodes) {
      // collections is not passed to the filter in useListState, so we have to
      // use the deprecated API of accessing childNodes directly on the node
      const filteredChildNodes = recursiveFilter(node.childNodes, filter);
      return filteredChildNodes.length > 0
        ? [{ ...node, childNodes: filteredChildNodes }]
        : [];
    } else {
      return node.type !== 'item' || filter(node.textValue) ? [node] : [];
    }
  });

const _ListBox = <T extends object>(
  {
    groupSelectedItemsOnTop,
    filter,
    onSelectionChange,
    resolveSelectAllToKeys,
    ...props
  }: ListBoxProps<T>,
  ref: ForwardedRef<HTMLUListElement>
): JSX.Element => {
  useThemeCheck();

  const maybeGroup = groupSelectedItemsOnTop ? groupSelectedItems : identity;
  const state = maybeGroup(
    useListState({
      selectionMode: 'single',
      ...props,
      // @ts-expect-error -- due to the SapphireSelectionManager, the type of onSelectionChange is different, sometimes it only returns Set<Keys> apart from the original "Selection" type. We decided to own this type error, instead of passing it down to the user.
      onSelectionChange,
      /* we don't want the other type of selectionBehavior, "replace" */
      selectionBehavior: 'toggle',
      filter: filter
        ? (nodes: Iterable<Node<T>>) => recursiveFilter<T>(nodes, filter)
        : undefined,
    })
  );

  patchSelectionManager(state.selectionManager, {
    resolveSelectAllToKeys,
  });

  return (
    <StatelessListBox
      {...props}
      ref={ref}
      state={state}
      disableSelectedStyles={props.selectionMode === 'none'}
    />
  );
};

export const ListBox = forwardRef(_ListBox) as <T extends object>(
  props: ListBoxProps<T> & { ref?: Ref<HTMLUListElement> }
) => React.ReactElement;

const identity = <T extends object>(x: T) => x;
